"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MemberBaseField = exports.OtherTypeUnitId = void 0;
const store_1 = require("../../exports/store");
const array_1 = require("../../utils/array");
const field_types_1 = require("../../types/field_types");
const view_types_1 = require("../../types/view_types");
const field_1 = require("./field");
const types_1 = require("../../types");
const utils_1 = require("../../model/utils");
var OtherTypeUnitId;
(function (OtherTypeUnitId) {
    OtherTypeUnitId["Self"] = "Self";
    OtherTypeUnitId["Alien"] = "Alien";
})(OtherTypeUnitId = exports.OtherTypeUnitId || (exports.OtherTypeUnitId = {}));
class MemberBaseField extends field_1.ArrayValueField {
    get apiMetaProperty() {
        return null;
    }
    /**
     * The underlying type of the cell value of the current field.
     */
    get basicValueType() {
        return field_types_1.BasicValueType.Array;
    }
    get innerBasicValueType() {
        return field_types_1.BasicValueType.String;
    }
    get acceptFilterOperators() {
        return [
            view_types_1.FOperator.Is,
            view_types_1.FOperator.IsNot,
            view_types_1.FOperator.Contains,
            view_types_1.FOperator.DoesNotContain,
            view_types_1.FOperator.IsEmpty,
            view_types_1.FOperator.IsNotEmpty,
            view_types_1.FOperator.IsRepeat,
        ];
    }
    /**
     * Get member unitIds, then sort and concatenate into strings
     */
    unitIdsToString(cellValue) {
        const unitIds = this.getUnitIds(cellValue);
        unitIds && unitIds.sort();
        return this.arrayValueToString(unitIds);
    }
    /**
     * Multiple members are grouped according to unitIds (orderInCellValueSensitive is false)
     * Solve problem 1: Many members have one-to-one correspondence but the order of members is different and they are divided into different groups
     * Solve problem 2: members with the same name but different unitId are assigned to the same group
     */
    compare(cellValue1, cellValue2, orderInCellValueSensitive) {
        if (!orderInCellValueSensitive) {
            if (this.eq(cellValue1, cellValue2))
                return 0;
            if (cellValue1 == null)
                return -1;
            if (cellValue2 == null)
                return 1;
            let str1 = this.unitIdsToString(cellValue1);
            let str2 = this.unitIdsToString(cellValue2);
            if (str1 === str2)
                return 0;
            if (str1 == null)
                return -1;
            if (str2 == null)
                return 1;
            str1 = str1.trim();
            str2 = str2.trim();
            return str1 === str2 ? 0 : (str1 > str2 ? 1 : -1);
        }
        return super.compare(cellValue1, cellValue2);
    }
    isMeetFilter(operator, cellValue, conditionValue) {
        const cv4filter = cellValue ? this.getUnitIds(cellValue) : [];
        // If it is judged in advance that it is empty or not, it should be judged directly as null, which is compatible with old data. 
        // There is no guarantee that cv comes with null, not an empty array.
        if (operator === view_types_1.FOperator.IsEmpty) {
            return cv4filter.length === 0;
        }
        if (operator === view_types_1.FOperator.IsNotEmpty) {
            return cv4filter.length > 0;
        }
        const conditionValue4Filter = conditionValue ? this.getUnitIds(conditionValue) : [];
        switch (operator) {
            case view_types_1.FOperator.Is: {
                const realConditionValue = this.conditionValueToReal(conditionValue4Filter);
                return cellValue != null && (0, array_1.isSameSet)(cv4filter, realConditionValue);
            }
            case view_types_1.FOperator.IsNot: {
                const realConditionValue = this.conditionValueToReal(conditionValue4Filter);
                return cellValue == null || !(0, array_1.isSameSet)(cv4filter, realConditionValue);
            }
            case view_types_1.FOperator.Contains: {
                const realConditionValue = this.conditionValueToReal(conditionValue4Filter);
                return cellValue != null && (0, array_1.hasIntersect)(cv4filter, realConditionValue);
            }
            case view_types_1.FOperator.DoesNotContain: {
                const realConditionValue = this.conditionValueToReal(conditionValue4Filter);
                return cellValue == null || !(0, array_1.hasIntersect)(cv4filter, realConditionValue);
            }
            default: {
                return super.isMeetFilter(operator, cv4filter, conditionValue4Filter);
            }
        }
    }
    conditionValueToReal(value) {
        const conditionValue = value.slice();
        const selfIndex = conditionValue.indexOf(OtherTypeUnitId.Self);
        // Identify the current user options and restore unitId/uuid
        if (selfIndex > -1) {
            const { uuid, unitId } = this.state.user.info;
            const selfUnitId = this.field.type === types_1.FieldType.Member ? unitId : uuid;
            conditionValue.splice(selfIndex, 1, selfUnitId || '');
        }
        return conditionValue;
    }
    /**
     * Inherited classes need to implement this method
     * @desc Get uuids/unitIds according to cellValue
     */
    getUnitIds(_cellValue) {
        return null;
    }
    /**
     * Inherited classes need to implement this method
      * @desc Get an array of corresponding names according to uuids/unitIds
     */
    getUnitNames(_cellValue) {
        return null;
    }
    /**
     * Inherited classes need to implement this method
      * @desc Get an array of corresponding unit information according to uuids/unitIds
     */
    getUnits(_cellValue) {
        return null;
    }
    /**
     * Inherited classes need to implement this method
      * @desc Get an array of corresponding names according to uuids/unitIds
     */
    getUnitList(_cellValue) {
        return null;
    }
    /**
     * @desc Get an array of corresponding names according to cellValue
     */
    cellValueToArray(cellValue) {
        if (!cellValue)
            return null;
        const unitIds = this.getUnitIds(cellValue);
        return this.getUnitNames(unitIds);
    }
    arrayValueToString(cellValues) {
        return cellValues && cellValues.length ? cellValues.join(', ') : null;
    }
    /**
     * @desc Get the string concatenated with the corresponding name according to cellValue
     */
    cellValueToString(cellValue, _cellToStringOption) {
        if (!cellValue)
            return null;
        return this.arrayValueToString(this.cellValueToArray([cellValue].flat()));
    }
    cellValueToStdValue(cellValue) {
        const stdValue = {
            sourceType: this.field.type,
            data: [],
        };
        if (!cellValue)
            return stdValue;
        const nameValues = this.cellValueToArray(cellValue);
        if (!nameValues)
            return stdValue;
        nameValues.forEach(item => {
            stdValue.data.push({
                text: item,
            });
        });
        return stdValue;
    }
    stdValueToCellValue(_stdValue) {
        return null;
    }
    defaultValueForCondition(condition) {
        if (this.field.type !== types_1.FieldType.Member) {
            return null;
        }
        const isMulti = this.field.property.isMulti;
        if (isMulti) {
            if ([view_types_1.FOperator.Is, view_types_1.FOperator.Contains].includes(condition.operator)) {
                return condition.value;
            }
            return null;
        }
        if (condition.operator === view_types_1.FOperator.Is ||
            (condition.operator === view_types_1.FOperator.Contains &&
                condition.value &&
                condition.value.length === 1)) {
            return condition.value;
        }
        return null;
    }
    defaultValue() {
        return null;
    }
    /**
     * api returns basic user information
     * @param cellValues array of uuids or uuid
     * @return
     * @author Zoe Zheng
     * @date 2021/3/15 3:03 pm
     */
    cellValueToApiStandardValue(cellValues) {
        var _a;
        if (cellValues == null)
            return null;
        const userMap = store_1.Selectors.getUserMap(this.state);
        if (userMap == null)
            return null;
        const uuid = cellValues;
        if (userMap.hasOwnProperty(uuid)) {
            return {
                id: uuid,
                name: userMap[uuid].name,
                avatar: (_a = userMap[uuid]) === null || _a === void 0 ? void 0 : _a.avatar,
            };
        }
        return null;
    }
    cellValueToApiStringValue(cellValues) {
        return this.cellValueToString(cellValues);
    }
    cellValueToOpenValue(cellValues) {
        return this.cellValueToApiStandardValue(cellValues);
    }
    openWriteValueToCellValue(openWriteValue) {
        if ((0, utils_1.isNullValue)(openWriteValue)) {
            return null;
        }
        const isSimple = openWriteValue.length && typeof openWriteValue[0] === 'string';
        // user on redux in iframe is different from user in main thread
        const userInfo = this.state.user.info || this.state.user;
        const transUserId = (id) => {
            // For openWriteValue, it may be the userId of the current user, so try to determine whether it is the userId of the current user.
            if (id === (userInfo === null || userInfo === void 0 ? void 0 : userInfo.userId) && userInfo.unitId) {
                return userInfo.unitId;
            }
            return id;
        };
        if (isSimple) {
            return openWriteValue.map(v => transUserId(v));
        }
        return openWriteValue.map(v => transUserId(v.id));
    }
}
exports.MemberBaseField = MemberBaseField;
//# sourceMappingURL=member_base_field.js.map